import type { TFunction } from 'i18next';
import { aiRefusal, isApiError, standingRefusal, type AiRefusal, type ApiError } from './errors';

/** Catalog-only copy: API prose is never a source of display text. */
const CODE_KEYS: Record<string, string> = {
  SUBSCRIPTION_IN_PROGRESS: 'errors.subscriptionPending',
  SUBSCRIPTION_EXISTS: 'errors.subscriptionExists',
  SESSION_EXPIRED: 'errors.signIn',
  NETWORK_ERROR: 'errors.network',
  UNAUTHORIZED: 'errors.signIn',
  FORBIDDEN: 'accounts.errorPermission',
  SUBSCRIPTION_REQUIRED: 'errors.subscribe',
  SUBSCRIPTION_EXPIRED: 'errors.subscribe',
  SUBSCRIPTION_CREATE_FAILED: 'subscribe.errorCreate',
  PAY_API_UNAVAILABLE: 'subscribe.errorPayApi',
  PLAN_MAPPING_CONFLICT: 'subscribe.errorPlanMapping',
  PROMOTION_NOT_FOUND: 'promo.invalid',
  PROMOTION_NOT_REDEEMABLE: 'promo.invalid',
  REFERRAL_NOT_FOUND: 'referral.notFound',
  REFERRAL_CONFIRM_REQUIRED: 'errors.referralConfirm',
  CONSENT_REQUIRED: 'subscribeDialog.consent.termsUpdated',
  SUBSCRIPTION_PENDING_ACTION: 'errors.subscriptionPending',
  PAYMENT_METHOD_UPDATE_FAILED: 'billing.errorCardUpdate',
  DELETION_ALREADY_REQUESTED: 'errors.deletionPending',
  CONFIRMATION_REQUIRED: 'settings.confirmEmailMismatch',
  AI_DISABLED: 'ai.off',
  WALLET_CHARGE_FAILED: 'card.declined.CARD_DECLINED',
  WALLET_CHARGE_UNAVAILABLE: 'aiWallet.chargeUnavailable',
  EXHAUSTED: 'promo.reason.EXHAUSTED',
  AI_BUDGET_EXCEEDED: 'ai.capped',
  IMPORT_FILE_TOO_LARGE: 'imports.errorTooLarge',
  IMPORT_UNPARSEABLE: 'imports.errorUnparseable',
  IMPORT_MAPPING_INVALID: 'imports.mappingInvalid',
  IMPORT_BATCH_STATE: 'imports.errorBatchState',
  IMPORT_PRESET_NOT_FOUND: 'imports.fit.formatGone',
  FX_RATE_UNAVAILABLE: 'imports.fxUnavailable',
  PROFILE_NAME_TAKEN: 'imports.errorNameTaken',
  PROFILE_NOT_FOUND: 'errors.notFound',
  USER_FROZEN: 'errors.userFrozen',
  REVIEW_NOT_DISMISSABLE: 'errors.reviewRequired',
  VALIDATION_FAILED: 'errors.checkFields',
  RATE_LIMITED: 'review.suggestRateLimited',
  UPSTREAM_UNAVAILABLE: 'errors.serviceUnavailable',
  HOSTNAME_NOT_REGISTERED: 'errors.configuration',
  TENANT_AUTH_NOT_CONFIGURED: 'errors.configuration',
  ACCOUNT_NOT_FOUND: 'errors.notFound',
  HOLDING_NOT_FOUND: 'holdings.notFound',
  ACCOUNT_CLOSED: 'accounts.errorClosed',
  ACCOUNT_CURRENCY_IMMUTABLE: 'accounts.errorCurrencyImmutable',
  OPENING_ALREADY_SET: 'accounts.errorOpeningAlreadySet',
  OPENING_AFTER_FIRST_LINE: 'accounts.errorOpeningAfterFirstLine',
  BEFORE_OPENING: 'accounts.errorBeforeOpening',
  OPENING_DIFFERS: 'imports.opening.errorDiffers',
  OPENING_PENDING: 'accounts.errorOpeningPending',
  BALANCE_CHECK_NOT_OPEN: 'accounts.errorBalanceCheckNotOpen',
  ACCOUNT_NOT_EMPTY: 'errors.accountNotEmpty',
  GRANTEE_NOT_SUBSCRIBER: 'accounts.errorGranteeNotSubscriber',
  CURRENCY_LOCKED: 'settings.currencyLockedError',
  TRANSACTION_NOT_FOUND: 'errors.notFound',
  TRANSACTION_VOID: 'errors.transactionVoid',
  TRANSACTION_HAS_ALLOCATIONS: 'accounts.errorVoidHasAllocations',
  ALLOCATION_NOT_FOUND: 'errors.notFound',
  ALLOCATION_EXCEEDS_REMAINDER: 'accounts.errorExceedsRemainder',
  ALLOCATION_SIGN_MISMATCH: 'accounts.errorSign',
  ALLOCATION_ALREADY_REVERSED: 'accounts.errorAlreadyReversed',
  SPLIT_TOTAL_CHANGED: 'allocations.errorSplitTotalChanged',
  INSUFFICIENT_TO_ASSIGN: 'allocations.errorInsufficientToAssign',
  ENVELOPE_HAS_CONTRIBUTIONS: 'envelope.errorHasContributions',
  CONCURRENT_UPDATE: 'errors.envelopeMoved',
  CARD_PAYMENT_NOT_ALLOCATABLE: 'accounts.errorCardPayment',
  PERMISSION_REQUIRED: 'accounts.errorPermission',
  IDEMPOTENCY_CONFLICT: 'accounts.errorIdempotency',
  SERIES_NOT_FOUND: 'errors.notFound',
  SERIES_INVALID: 'planned.errorInvalid',
  PLAN_EXCEEDS_OCCURRENCE: 'errors.planExceeds',
  PLAN_LINE_LIKELY_DUPLICATE: 'errors.planLineLikelyDuplicate',
  OCCURRENCE_RESOLVED: 'planned.errorResolved',
  OCCURRENCE_NOT_FOUND: 'planned.errorNotFound',
  MATCH_AMBIGUOUS: 'errors.matchAmbiguous',
  NOT_AUTO_POST: 'planned.errorNotAutoPost',
  COVERAGE_BACKWARD: 'accounts.coverageBackward',
  ENVELOPE_KIND_MISMATCH: 'planLines.errorKind',
  MEETING_NOT_FOUND: 'meetings.errorNotFound',
  INVITEE_NOT_SHARER: 'meetings.errorInvitee',
  INVITEE_NOT_IN_POOL: 'meetings.errorNotInPoolAny',
  MEETING_SCHEDULE_NOT_ALLOWED: 'meetings.scheduleNotAllowed',
  NOTE_VERSION_CONFLICT: 'meetings.noteConflict',
  SUMMARY_NO_INPUT: 'errors.summaryNoInput',
  RULE_INVALID: 'errors.checkFields',
  RULE_NOT_FOUND: 'errors.notFound',
  RULE_EXISTS: 'rules.nameTaken',
  REORDER_SET_MISMATCH: 'rules.reorderMismatch',
  CARD_DECLINED: 'card.declined.CARD_DECLINED',
  INTERVAL_NOT_OFFERED: 'errors.intervalUnavailable',
  CONNECTOR_UNAVAILABLE: 'connections.unavailable',
  CONNECTOR_NOT_FOUND: 'errors.notFound',
  CONNECTOR_MAPPING_INVALID: 'errors.checkFields',
  CONNECTOR_INSTITUTION_MISMATCH: 'connections.institutionMismatch',
  CONNECTOR_NOT_REAUTHABLE: 'connections.notReauthable',
  CONNECTOR_SESSION_NOT_FOUND: 'connections.sessionNotFound',
  AUTOMATION_KEY_UNKNOWN: 'automation.staleCatalogue',
  // tmm-api 0146 (TMMAPP-0110).
  VIRTUAL_ACCOUNT_READ_ONLY: 'roles.readOnlyNow',
  VIRTUAL_ACCOUNT_LOCKED: 'roles.lockedNow',
  VIRTUAL_ACCOUNT_REQUIRED: 'virtualAccounts.required',
  ALLOCATION_CURRENCY_MISMATCH: 'allocations.errorCurrencyMismatch',
  VERSION_CONFLICT: 'meetings.summaryConflict',
  // TMMAPI-0156 U2 / 0157 (TMMAPP-0119).
  TAX_LOCATION_REQUIRED: 'billing.taxLocationRequired',
  NOT_A_PAYBACK: 'reimb.notAPayback.unknown',
  TRANSFER_NOT_ALLOCATABLE: 'reimb.transferNotAllocatable',
  // TMMAPI-0179 (TMMAPP-0140 item 3). The confirm paths ask in place; the sentence is the fallback.
  REIMBURSEMENT_TARGET_REQUIRED: 'reimb.targetRequired',
  // TMMAPP-0162 item 7: the queue says it in place of the pair card; the sentence is the fallback.
  REIMBURSEMENT_EXCEEDS_EXPENSE: 'reimb.alreadyPaidBack',
  MASK_MISMATCH: 'review.missingAccount.maskMismatch',
  // TMMAPI-0165 (TMMAPP-0133). The wire actions handle these in place; the sentences are the fallback.
  NOT_A_WIRE: 'errors.notAWire',
  OWN_ACCOUNT_WIRE: 'errors.ownAccountWire',
  // TMMAPI-0159 (TMMAPP-0122 item 23). A charge surface adds BILLING_CONFIGURATION's Reference line
  // (`ReferenceLine`), as the error state does.
  WALLET_DISPUTE_HOLD: 'aiWallet.disputeHold',
  BILLING_CONFIGURATION: 'billing.configurationError',
  // TMMAPI-0160 U3 (TMMAPP-0124). The alias dialog shows PATTERN_INVALID's reason at the field.
  PATTERN_INVALID: 'aliasDialog.invalid.SYNTAX',
  PAYEE_ARCHIVED: 'payee.archivedError',
  // TMMAPI-0174 (TMMAPP-0138 item 2): never "AI is off". A surface with its own sentence for an
  // unusable answer names itself (`AI_SURFACE_KEYS`).
  AI_NO_SUGGESTION: 'ai.noSuggestion',
  AI_PROVIDER_UNAVAILABLE: 'ai.providerUnavailable',
  PAYEE_SUGGESTION_DECIDED: 'naming.cantBringBack',
  PAYEE_SUGGESTION_NOT_FOUND: 'naming.noLongerNeeded',
  // TMMAPI-0193 U1 (TMMAPP-0163). The card reads a missing current run as none; the sentences are
  // the fallback.
  PAYEE_NAMING_BUSY: 'naming.run.busy',
  PAYEE_NAMING_RUN_NOT_FOUND: 'naming.run.notFound',
  // TMMAPI-0195 U2 (TMMAPP-0163 item 12): the page re-reads, so the line shows what stands now.
  INSTITUTION_PROPOSAL_STATE: 'institutions.proposal.changed',
  // TMMAPI-0195 U3 (TMMAPP-0164 item 12): the row leaves quietly; this is the fallback.
  PAYEE_TEXT_NOT_FOUND: 'naming.unnamed.notFound',
  // TMMAPI-0181 U1 / U2 (TMMAPP-0142 U1). The assistant shows these in place; the sentences are
  // also its fallback.
  ASSISTANT_SCOPE_INVALID: 'assistant.error.scopeInvalid',
  ASSISTANT_SESSION_ARCHIVED: 'assistant.archivedNotice',
  ASSISTANT_ACCESS_ENDED: 'assistant.accessEnded',
  ASSISTANT_TURN_RUNNING: 'assistant.error.turnRunning',
  ASSISTANT_TURN_NOT_WAITING: 'assistant.error.turnNotWaiting',
  ASSISTANT_BUSY: 'assistant.error.busy',
  ASSISTANT_GUEST_NOT_ALLOWED: 'assistant.share.guestNotAllowed',
  // TMMAPI-0195 U4 (TMMAPP-0164 item 14): the chat explains by the served reason; this is the fallback.
  ASSISTANT_ASK_NOT_ALLOWED: 'assistant.cantAsk.generic',
  ASSISTANT_SESSION_NOT_FOUND: 'assistant.error.sessionNotFound',
  ASSISTANT_TURN_NOT_FOUND: 'assistant.error.turnNotFound',
  // TMMAPI-0181 U7 (TMMAPP-0142 U5). The composer words the 413 per `limitKind`; these are the
  // fallbacks.
  ASSISTANT_ATTACHMENT_TOO_LARGE: 'assistant.attach.error.tooLarge',
  ASSISTANT_ATTACHMENT_TYPE: 'assistant.attach.error.type',
  ASSISTANT_ATTACHMENT_NOT_FOUND: 'assistant.attach.error.notFound',
  ASSISTANT_ATTACHMENT_ATTACHED: 'assistant.attach.error.attached',
  // TMMAPI-0181 U4 / U5: Help & feedback. The UI never reaches the fixed session's 409.
  ASSISTANT_SUPPORT_SESSION_FIXED: 'support.error.sessionFixed',
  // TMMAPI-0184 U3: the chat names the payer in place; this is the fallback, with no figure.
  PAYER_UNAVAILABLE: 'assistant.payer.unavailableGeneric',
  HELP_ARTICLE_NOT_FOUND: 'support.help.articleNotFound',
  HELP_IMAGE_NOT_FOUND: 'support.help.imageNotFound',
  TICKET_NOT_FOUND: 'support.tickets.notFound',
  TICKET_TRANSITION_INVALID: 'support.tickets.cantClose',
  ANNOUNCEMENT_NOT_FOUND: 'support.announcements.notFound',
  PERSON_NOT_FOUND: 'errors.notFound',
  // TMMAPI-0183 U1 (TMMAPP-0142 U7): Connected AI tools.
  AGENT_CONNECTION_NOT_FOUND: 'agentTools.notFound',
};

/** An AI surface whose unusable answer (`AI_NO_SUGGESTION`) reads its own sentence. */
export type AiSurface = 'IMPORT_MAPPING';

const AI_SURFACE_KEYS: Record<AiSurface, Partial<Record<string, string>>> = {
  IMPORT_MAPPING: { AI_NO_SUGGESTION: 'imports.aiNoSuggestion' },
};

/** A `403 FORBIDDEN` that names the role it needed (TMMAPI-0146) reads the role's sentence. */
const REQUIRED_ROLE_KEYS: Record<string, string> = {
  needManager: 'roles.needManager',
  needOwner: 'roles.needOwner',
};

/**
 * The gate's reasons that have their own sentence (TMMAPI-0151 / 0152); where a host shows the
 * prompt, the wallet's are `AiWalletPrompt`'s.
 */
const AI_REASON_KEYS: Record<string, string> = {
  GLOBAL: 'ai.reason.GLOBAL',
  FEATURE: 'ai.reason.FEATURE',
  USER: 'ai.reason.USER',
  DAILY_CAP: 'ai.reason.DAILY_CAP',
  WALLET_EMPTY: 'ai.reason.WALLET_EMPTY',
  NO_PRICE: 'aiWallet.prompt.noPrice',
  PERSON_CAP: 'aiWallet.prompt.personCapPlain',
  // TMMAPI-0152: the admin's suspension, and MM-D-133's debt, in-flight limit and standing.
  SUSPENDED: 'ai.reason.SUSPENDED',
  AI_DEBT: 'ai.reason.AI_DEBT',
  BUSY: 'ai.reason.BUSY',
  SUBSCRIPTION_INACTIVE: 'ai.reason.SUBSCRIPTION_INACTIVE',
  // TMMAPI-0160 U3: a payee kept out of AI; nothing was sent or metered.
  PAYEE_EXCLUDED: 'ai.reason.PAYEE_EXCLUDED',
};

/**
 * Why AI refused, in one sentence: the served reason's own copy, and the generic "AI is off" only
 * when no known reason is served. The one place `ai.off` is read.
 */
export function aiDisabledCopy(t: TFunction, refusal: AiRefusal): string {
  const key = refusal.reason !== null ? AI_REASON_KEYS[refusal.reason] : undefined;
  return t(key ?? 'ai.off');
}

/** `409 NOT_A_PAYBACK`'s served reasons that have their own sentence (TMMAPI-0157, TMM-B3). */
const NOT_A_PAYBACK_KEYS: Record<string, string> = {
  OWN_TRANSFER: 'reimb.notAPayback.OWN_TRANSFER',
  CARD_PAYMENT: 'reimb.notAPayback.CARD_PAYMENT',
  INTEREST: 'reimb.notAPayback.INTEREST',
  REVOLVING_CREDIT: 'reimb.notAPayback.REVOLVING_CREDIT',
};

/** A manual link refused as no payback: the reason's sentence, else "This can't be a payback." */
function notAPaybackCopy(t: TFunction, error: ApiError): string {
  const backend = error.details.backend;
  const reason = typeof backend === 'object' && backend !== null ? backend.reason : undefined;
  const key =
    typeof reason === 'string' && Object.hasOwn(NOT_A_PAYBACK_KEYS, reason)
      ? NOT_A_PAYBACK_KEYS[reason]
      : undefined;
  return t(key ?? 'reimb.notAPayback.unknown');
}

/**
 * The catalog copy for a failure: the key its code maps to, else the caller's `fallbackKey`.
 * `values` interpolate into whichever is chosen (a fallback that names its object, "couldn't merge
 * {{name}}"); the mapped keys take none. `surface` picks an AI surface's own sentence where it has
 * one (TMMAPP-0138 item 2).
 */
export function errorCopy(
  t: TFunction,
  error: unknown,
  fallbackKey: string,
  values?: Record<string, string | number>,
  surface?: AiSurface,
): string {
  const ai = aiRefusal(error);
  if (ai !== null) return aiDisabledCopy(t, ai);
  if (isApiError(error) && error.code === 'NOT_A_PAYBACK') return notAPaybackCopy(t, error);
  const refusal = standingRefusal(error);
  const key =
    (refusal !== null ? REQUIRED_ROLE_KEYS[refusal] : undefined) ??
    (isApiError(error) && surface !== undefined
      ? AI_SURFACE_KEYS[surface][error.code]
      : undefined) ??
    (isApiError(error) && Object.hasOwn(CODE_KEYS, error.code) ? CODE_KEYS[error.code] : undefined);
  return t(key ?? fallbackKey, { ...values });
}
